import React, { useState, useEffect } from "react";
import { Table, Input, Button, Card, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { dependenciasApi } from "../../services/api";
import DependenciaFormModal from "./DependenciaFormModal";
import DeleteConfirmModal from "../DeleteConfirmModal";

const TablaDependencias = () => {
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDependencia, setEditingDependencia] = useState(null);
  const [dependencias, setDependencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [dependenciaToDelete, setDependenciaToDelete] = useState(null);

  // Cargar datos al iniciar el componente
  useEffect(() => {
    fetchDependencias()
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error cargando datos iniciales:", error);
        toast.error("Error al cargar los datos", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
      });
  }, []);

  // Función para obtener dependencias
  const fetchDependencias = async () => {
    try {
      const response = await dependenciasApi.getAll();
      if (response.data.status) {
        const dependenciasData = response.data.dependencias.map((item) => ({
          key: item.id.toString(),
          id: item.id,
          dependencia: item.dependencia,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));
        setDependencias(dependenciasData);
      }
    } catch (error) {
      console.error("Error obteniendo dependencias:", error);
      toast.error("Error al cargar las dependencias", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Buscar
        </Button>
        <Button
          onClick={() => clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Limpiar
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: <div className="font-medium">ID</div>,
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: <div className="font-medium">Dependencia</div>,
      dataIndex: "dependencia",
      key: "dependencia",
      ...getColumnSearchProps("dependencia"),
      width: "70%",
    },
    {
      title: <div className="font-medium text-center">Acciones</div>,
      key: "acciones",
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Tooltip title="Editar dependencia">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              type="primary"
              size="middle"
              className="bg-blue-500 hover:bg-blue-700"
            />
          </Tooltip>
          <Tooltip title="Eliminar dependencia">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              type="primary"
              danger
              size="middle"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    fetchDependencias();
  };

  const showModal = () => {
    setIsModalVisible(true);
    setEditingDependencia(null);
  };

  const handleEdit = (dependencia) => {
    setEditingDependencia(dependencia);
    setIsModalVisible(true);
  };

  const handleDelete = (dependencia) => {
    setDependenciaToDelete(dependencia);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await dependenciasApi.delete(dependenciaToDelete.id);
      toast.success(`Dependencia "${dependenciaToDelete.dependencia}" eliminada correctamente`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setDeleteModalVisible(false);
      setDependenciaToDelete(null);
      fetchDependencias(); // Recargar la lista de dependencias
    } catch (error) {
      console.error("Error eliminando dependencia:", error);
      let errorMsg = "Error al eliminar la dependencia";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    setEditingDependencia(null);
    fetchDependencias(); // Recargar la lista después de un cambio exitoso
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
        <Spin size="large" tip="Cargando dependencias..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Administración de Dependencias
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Nueva Dependencia
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <Input
              placeholder="Buscar dependencia..."
              className="w-72"
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            type="default"
            className="h-10"
          >
            Reiniciar filtros
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={dependencias.filter(
            (item) =>
              item.dependencia.toLowerCase().includes(searchText.toLowerCase())
          )}
          rowClassName={() =>
            "hover:bg-blue-50 transition-colors cursor-pointer"
          }
          scroll={{ y: "calc(100vh - 350px)" }}
          pagination={{
            total: dependencias.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} dependencias`,
          }}
          className="border border-gray-200 rounded-md"
        />

        {/* Modal para crear/editar dependencia */}
        <DependenciaFormModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={handleModalSuccess}
          editingDependencia={editingDependencia}
        />

        {/* Modal de confirmación para eliminar dependencia */}
        <DeleteConfirmModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
          title="Eliminar Dependencia"
          itemDescription="la dependencia"
          itemName={dependenciaToDelete ? dependenciaToDelete.dependencia : ""}
        />
      </Card>
    </div>
  );
};

export default TablaDependencias;